/**
 * The typed API client.
 *
 * `openapi-fetch` over the generated schema: paths, parameters and response bodies are
 * all checked against what the API publishes. There are no hand-written fetch calls in
 * this codebase, which is the point — a renamed field is a compile error rather than an
 * undefined in front of a user.
 *
 * Base URL: the deployed client calls the API host directly and the API allow-lists the two
 * Static Web Apps origins. Since 2026-10-29 the API is also a linked backend of the Static
 * Web App (Standard SKU), so `/api` on the web domain reaches it — that is the address the
 * API Access page gives people and tools. The web app itself keeps the direct host: it
 * avoids the proxy's request-size and time limits for document uploads.
 *
 * Locally the Vite dev server proxies `/api`, so `VITE_API_ORIGIN` stays unset and
 * requests are same-origin during development.
 */

import createClient, { type Middleware } from 'openapi-fetch'

import { acquireToken } from '@/lib/auth'
import type { paths } from '@/lib/api/schema'

/** The API's error shape. Every failure arrives in this form. */
export interface ApiErrorBody {
  code: string
  message: string
  correlationId: string | null
  details: Record<string, unknown> | null
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly correlationId: string | null
  readonly details: Record<string, unknown> | null

  constructor(status: number, body: Partial<ApiErrorBody> | null) {
    super(body?.message ?? 'The request could not be completed.')
    this.name = 'ApiError'
    this.status = status
    this.code = body?.code ?? 'unknown_error'
    this.correlationId = body?.correlationId ?? null
    this.details = body?.details ?? null
  }

  /** True when the record changed underneath the user since they loaded it. */
  get isConcurrencyConflict(): boolean {
    return this.code === 'concurrency_conflict'
  }
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await acquireToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },

  async onResponse({ response }) {
    if (response.ok) return response

    // Surface the API's structured error, including the correlation id — that id is how
    // support joins a user's report to the server log for the same operation.
    let body: Partial<ApiErrorBody> | null = null
    try {
      body = (await response.clone().json()) as Partial<ApiErrorBody>
    } catch {
      body = null
    }
    throw new ApiError(response.status, body)
  },
}

export const api = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_ORIGIN ?? '/',
})
api.use(authMiddleware)
